import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import './EmailModal.css';

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  supplierIndex: number;
  supplierName: string;
  supplierEmail: string;
  onEmailSent: () => void;
  // New props for multi-recipient support
  availableEmails?: string[]; // List of available email addresses for this supplier
  requestData?: any; // Request data to get supplier information
}

const EmailModal: React.FC<EmailModalProps> = ({
  isOpen,
  onClose,
  requestId,
  supplierIndex,
  supplierName,
  supplierEmail,
  onEmailSent,
  availableEmails = [],
  requestData = null
}) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de' | 'tr'>('en');
  
  // Editable supplier fields
  const [editableSupplierName, setEditableSupplierName] = useState(supplierName);
  const [editableSupplierEmail, setEditableSupplierEmail] = useState(supplierEmail);
  const [editableAdditionalEmails, setEditableAdditionalEmails] = useState<string[]>([]);
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);
  const [isUpdatingSupplier, setIsUpdatingSupplier] = useState(false);
  const [originalSupplierName, setOriginalSupplierName] = useState(supplierName); // Store original name
  
  // Multi-recipient email state
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [allAvailableEmails, setAllAvailableEmails] = useState<string[]>([]);

  // Function to fetch approved supplier data
  const fetchApprovedSupplierData = async (supplierName: string) => {
    console.log('🔍 fetchApprovedSupplierData called with supplierName:', supplierName);
    try {
      const response = await fetch(API_ENDPOINTS.SUPPLIERS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 API response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('🔍 API result:', result);
        
        if (result.success && result.data) {
          console.log('🔍 Found suppliers:', result.data.length);
          
          // Find the supplier by name
          const supplier = result.data.find((s: any) => s.name === supplierName);
          console.log('🔍 Found supplier:', supplier);
          
          if (supplier) {
            let mainEmail = '';
            let additionalEmails: string[] = [];
            
            console.log('🔍 Supplier emails field:', supplier.emails);
            console.log('🔍 Supplier email field:', supplier.email);
            
            // Check if supplier has emails array (new format)
            if (supplier.emails && Array.isArray(supplier.emails) && supplier.emails.length > 0) {
              // Use emails array: first is main, rest are additional
              mainEmail = supplier.emails[0];
              additionalEmails = supplier.emails.slice(1);
              console.log('🔍 Using emails array - mainEmail:', mainEmail, 'additionalEmails:', additionalEmails);
            } else if (supplier.email) {
              // Fallback to single email field
              mainEmail = supplier.email;
              additionalEmails = [];
              console.log('🔍 Using single email field - mainEmail:', mainEmail);
            }
            
            console.log('🔍 Final mainEmail:', mainEmail);
            console.log('🔍 Final additionalEmails:', additionalEmails);
            
            if (mainEmail) {
              console.log('🔍 Setting editableSupplierEmail to:', mainEmail);
              setEditableSupplierEmail(mainEmail);
              console.log('🔍 Setting editableAdditionalEmails to:', additionalEmails);
              setEditableAdditionalEmails(additionalEmails);
              
              // Update available emails and selected recipients
              const allEmails = [mainEmail, ...additionalEmails];
              console.log('🔍 Setting allAvailableEmails to:', allEmails);
              setAllAvailableEmails(allEmails);
              console.log('🔍 Setting selectedRecipients to:', [mainEmail]);
              setSelectedRecipients([mainEmail]);
            } else {
              console.log('❌ No mainEmail found!');
            }
          } else {
            console.log('❌ Supplier not found!');
          }
        } else {
          console.log('❌ API result not successful or no suppliers');
        }
      } else {
        console.log('❌ API response not ok');
      }
    } catch (error) {
      console.error('❌ Error fetching approved supplier data:', error);
    }
  };

  // Update editable fields when props change
  useEffect(() => {
    console.log('🔍 useEffect triggered with:', { supplierName, supplierEmail, requestData: !!requestData, supplierIndex });
    console.log('🔍 supplierName prop:', supplierName);
    console.log('🔍 supplierEmail prop:', supplierEmail);
    console.log('🔍 supplierIndex prop:', supplierIndex);
    
    // Prevent body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.top = '0';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.bottom = '0';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.bottom = '';
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.bottom = '';
      document.body.classList.remove('modal-open');
    };
    
    // If supplierName is undefined but we have requestData and supplierIndex, try to get it from local suppliers
    if (!supplierName && requestData && requestData.analysis_result && requestData.analysis_result.local_suppliers && supplierIndex >= 0) {
      const localSupplier = requestData.analysis_result.local_suppliers[supplierIndex];
      if (localSupplier && localSupplier.supplier) {
        console.log('🔍 Found local supplier from requestData:', localSupplier.supplier);
        console.log('🔍 Local supplier email_sent status:', localSupplier.email_sent);
        console.log('🔍 Local supplier email_sent_at:', localSupplier.email_sent_at);
        
        setEditableSupplierName(localSupplier.supplier.name);
        setEditableSupplierEmail(localSupplier.supplier.email);
        
        // Set additional emails if available
        if (localSupplier.supplier.emails && localSupplier.supplier.emails.length > 0) {
          const additionalEmails = localSupplier.supplier.emails.filter((email: string) => email !== localSupplier.supplier.email);
          setEditableAdditionalEmails(additionalEmails);
        } else {
          setEditableAdditionalEmails([]);
        }
        
        // Load email template for this supplier
        console.log('🔍 Loading email template for local supplier:', localSupplier.supplier.name);
        loadEmailTemplate(currentLanguage);
        return; // Exit early since we've set the data
      }
    }
    
    setEditableSupplierName(supplierName);
    setEditableSupplierEmail(supplierEmail);
    
    // Load additional emails from request data
    if (requestData) {
      console.log('🔍 requestData exists, checking for approved suppliers');
      
      // For approved suppliers, we need to get the actual supplier data
      if (requestData.selected_approved_suppliers && requestData.selected_approved_suppliers.length > 0) {
        console.log('🔍 Found selected_approved_suppliers:', requestData.selected_approved_suppliers);
        
        // Find the supplier in the selected_approved_suppliers array
        const supplierNameFromProps = supplierName;
        const supplier = requestData.selected_approved_suppliers.find((s: any) => s === supplierNameFromProps);
        
        console.log('🔍 Looking for supplier:', supplierNameFromProps, 'Found:', supplier);
        
        if (supplier) {
          console.log('🔍 Calling fetchApprovedSupplierData for:', supplierNameFromProps);
          // Fetch the actual supplier data from the backend
          fetchApprovedSupplierData(supplierNameFromProps);
        } else {
          console.log('❌ Supplier not found in selected_approved_suppliers');
        }
      } else {
        console.log('🔍 No selected_approved_suppliers, using AI/local logic');
        // For AI/local suppliers, use the existing logic
        const emails = getSupplierEmails(requestData, supplierIndex);
        const additionalEmails = emails.filter(email => email !== supplierEmail);
        setEditableAdditionalEmails(additionalEmails);
        
        // Load email template for AI/local supplier
        if (supplierName && supplierEmail) {
          console.log('🔍 Loading email template for AI/local supplier:', supplierName);
          
          // Check email tracking status
          if (requestData.analysis_result) {
            if (requestData.analysis_result.ai_suppliers && supplierIndex < requestData.analysis_result.ai_suppliers.length) {
              const aiSupplier = requestData.analysis_result.ai_suppliers[supplierIndex];
              console.log('🔍 AI supplier email_sent status:', aiSupplier.email_sent);
              console.log('🔍 AI supplier email_sent_at:', aiSupplier.email_sent_at);
            } else if (requestData.analysis_result.local_suppliers && supplierIndex < requestData.analysis_result.local_suppliers.length) {
              const localSupplier = requestData.analysis_result.local_suppliers[supplierIndex];
              console.log('🔍 Local supplier email_sent status:', localSupplier.email_sent);
              console.log('🔍 Local supplier email_sent_at:', localSupplier.email_sent_at);
            }
          }
          
          loadEmailTemplate(currentLanguage);
        }
      }
    } else {
      console.log('❌ No requestData provided');
    }
  }, [supplierName, supplierEmail, requestData, supplierIndex, isOpen]);

  // Load available emails for multi-recipient selection
  useEffect(() => {
    if (isOpen && requestData) {
      // For approved suppliers, use the editable data
      if (requestData.selected_approved_suppliers && requestData.selected_approved_suppliers.length > 0) {
        // Use editable supplier email and additional emails
        const emails = [editableSupplierEmail, ...editableAdditionalEmails].filter(email => email && email.trim() !== '');
        setAllAvailableEmails(emails);
        
        // Default to selecting the main supplier email
        if (editableSupplierEmail) {
          setSelectedRecipients([editableSupplierEmail]);
        }
      } else {
        // For AI/local suppliers, use the existing logic
        const emails = getSupplierEmails(requestData, supplierIndex);
        setAllAvailableEmails(emails);
        
        // Default to selecting the main supplier email
        if (emails.length > 0) {
          setSelectedRecipients([supplierEmail]);
        }
      }
    }
  }, [isOpen, requestData, supplierIndex, supplierEmail, editableSupplierEmail, editableAdditionalEmails]);

  // Update available emails when editable supplier data changes
  useEffect(() => {
    if (isOpen && (editableSupplierEmail || editableAdditionalEmails.length > 0)) {
      // Get all email addresses using editable data
      const emails = getSupplierEmails(requestData, supplierIndex, true);
      setAllAvailableEmails(emails);
      
      // Update selected recipients to include the main editable email
      if (editableSupplierEmail) {
        setSelectedRecipients(prev => {
          const newRecipients = [editableSupplierEmail];
          // Add additional emails if they exist
          if (editableAdditionalEmails.length > 0) {
            newRecipients.push(...editableAdditionalEmails.filter(email => email && email.trim() !== ''));
          }
          return Array.from(new Set(newRecipients));
        });
      }
    }
  }, [isOpen, editableSupplierEmail, editableAdditionalEmails]);

  // Function to get all email addresses for a supplier
  const getSupplierEmails = (requestData: any, supplierIndex: number, useEditableData: boolean = false): string[] => {
    if (!requestData) return [];
    
    // If we're using editable data (for email sending), use the current editable values
    if (useEditableData) {
      const emails: string[] = [];
      
      // Main email from editable state
      if (editableSupplierEmail) {
        emails.push(editableSupplierEmail);
      }
      
      // Additional emails from editable state
      if (editableAdditionalEmails && editableAdditionalEmails.length > 0) {
        emails.push(...editableAdditionalEmails.filter(email => email && email.trim() !== ''));
      }
      
      return Array.from(new Set(emails));
    }
    
    // Check if this is an approved supplier (has selected_approved_suppliers)
    if (requestData.selected_approved_suppliers && requestData.selected_approved_suppliers.length > 0) {
      // For approved suppliers, we need to get the supplier data from the suppliers collection
      // This will be handled by the backend when loading the email template
      // For now, return empty array and let the backend handle it
      return [];
    }
    
    // Otherwise, get from request data (for initial loading)
    const analysisResult = requestData.analysis_result || {};
    const aiSuppliers = analysisResult.ai_suppliers || [];
    const localSuppliers = analysisResult.local_suppliers || [];
    
    let supplier = null;
    if (supplierIndex < aiSuppliers.length) {
      supplier = aiSuppliers[supplierIndex];
    } else {
      const localIndex = supplierIndex - aiSuppliers.length;
      supplier = localSuppliers[localIndex];
    }
    
    if (!supplier) return [];
    
    // Collect all email addresses from the supplier
    const emails: string[] = [];
    
    // Check if supplier has emails array (new format)
    if (supplier.emails && Array.isArray(supplier.emails) && supplier.emails.length > 0) {
      // First email is main email, rest are additional
      emails.push(...supplier.emails);
    } else {
      // Legacy format - single email field
      if (supplier.email) {
        emails.push(supplier.email);
      }
      
      // Additional emails (if any)
      if (supplier.additional_emails && Array.isArray(supplier.additional_emails)) {
        emails.push(...supplier.additional_emails);
      }
    }
    
    // Contact person emails (if any)
    if (supplier.contact_persons && Array.isArray(supplier.contact_persons)) {
      supplier.contact_persons.forEach((person: any) => {
        if (person.email) {
          emails.push(person.email);
        }
      });
    }
    
    // Remove duplicates
    return Array.from(new Set(emails));
  };

  const loadEmailTemplate = async (language?: 'en' | 'de' | 'tr') => {
    setIsLoadingTemplate(true);
    setError('');

    try {
      const baseUrl = API_ENDPOINTS.GET_EMAIL_TEMPLATE(requestId, supplierIndex.toString());
      // Only add language parameter if explicitly provided
      const url = language ? `${baseUrl}?language=${language}` : baseUrl;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        // If no language parameter was provided, use region-based mapping
        if (!language && result.data.supplier_region) {
          const mappedLanguage = mapRegionToLanguage(result.data.supplier_region);
          
          // If the mapped language is different from what backend returned, reload with correct language
          if (mappedLanguage !== result.data.language) {
            setCurrentLanguage(mappedLanguage);
            // Reload template with correct language
            loadEmailTemplate(mappedLanguage);
            return;
          }
        }
        
        // Localize field names in the email content
        let localizedContent = result.data.content;
        const fieldNames = getFieldNames(result.data.language);
        
        // Replace field names in the content
        Object.entries(fieldNames).forEach(([englishName, localizedName]) => {
          if (englishName !== localizedName) {
            const regex = new RegExp(`<td[^>]*>${englishName}</td>`, 'g');
            localizedContent = localizedContent.replace(regex, `<td style="border: 1px solid #ddd; padding: 12px; font-weight: bold;">${localizedName}</td>`);
          }
        });
        
        // Add mail IDs to subject
        const companyMailId = requestData?.company_mail_id ? String(requestData.company_mail_id).padStart(2, '0') : '11';
        const requestMailId = requestData?.mail_id ? String(requestData.mail_id).padStart(3, '0') : '010';
        const subjectWithMailIds = `${companyMailId}${requestMailId} - ${result.data.subject}`;
        
        setEmailSubject(subjectWithMailIds);
        setEmailContent(localizedContent);
        setCurrentLanguage(result.data.language);
      } else {
        setError(result.message || t('components.email_modal.failed_load_template'));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('components.email_modal.unknown_error');
      setError(t('components.email_modal.error_load_template', { message: errorMessage }));
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  // Function to map various country names to standardized region codes
  const mapRegionToLanguage = (region: string): 'en' | 'de' | 'tr' => {
    const regionUpper = region.toUpperCase();
    
    // German-speaking regions
    const germanRegions = [
      'DE', 'DACH', 'AT', 'CH', // Standard codes
      'GERMANY', 'DEUTSCHLAND', 'ALMANYA', // Germany variations
      'AUSTRIA', 'ÖSTERREICH', 'AVUSTURYA', // Austria variations  
      'SWITZERLAND', 'SCHWEIZ', 'İSVİÇRE', 'SWISS', // Switzerland variations
      'LIECHTENSTEIN', 'LÜKSEMBURG', 'LUXEMBOURG' // Other German-speaking countries
    ];
    
    // Turkish regions
    const turkishRegions = [
      'TR', 'TURKEY', 'TÜRKIYE', 'TURKİYE' // Turkey variations
    ];
    
    if (germanRegions.includes(regionUpper)) {
      return 'de';
    } else if (turkishRegions.includes(regionUpper)) {
      return 'tr';
    } else {
      return 'en';
    }
  };

  // Field name translations
  const getFieldNames = (language: 'en' | 'de' | 'tr') => {
    const fieldNames = {
      en: {
        'Product Name': 'Product Name',
        'Product Brand': 'Product Brand',
        'Quantity': 'Quantity',
        'Delivery Time': 'Delivery Time',
        'Delivery Address': 'Delivery Address',
        'Additional Spec': 'Additional Spec'
      },
      de: {
        'Product Name': 'Produktname',
        'Product Brand': 'Produktmarke',
        'Quantity': 'Menge',
        'Delivery Time': 'Lieferzeit',
        'Delivery Address': 'Lieferadresse',
        'Additional Spec': 'Zusätzliche Spezifikation'
      },
      tr: {
        'Product Name': 'Ürün Adı',
        'Product Brand': 'Ürün Markası',
        'Quantity': 'Miktar',
        'Delivery Time': 'Teslimat Süresi',
        'Delivery Address': 'Teslimat Adresi',
        'Additional Spec': 'Ek Özellik'
      }
    };
    
    return fieldNames[language] || fieldNames.en;
  };

  const handleChangeLanguage = () => {
    let newLanguage: 'en' | 'de' | 'tr';
    if (currentLanguage === 'en') {
      newLanguage = 'de';
    } else if (currentLanguage === 'de') {
      newLanguage = 'tr';
    } else {
      newLanguage = 'en';
    }
    loadEmailTemplate(newLanguage);
  };

  const handleUpdateSupplier = async () => {
    if (!editableSupplierName || !editableSupplierName.trim() || !editableSupplierEmail || !editableSupplierEmail.trim()) {
      setError(t('components.email_modal.supplier_name_email_required'));
      return;
    }

    setIsUpdatingSupplier(true);
    setError('');

    try {
      // Check which type of supplier this is
      let isAISupplier = false;
      let isLocalSupplier = false;
      let isApprovedSupplier = false;

      if (requestData) {
        // Check if it's an AI supplier
        if (requestData.analysis_result && requestData.analysis_result.ai_suppliers) {
          isAISupplier = requestData.analysis_result.ai_suppliers.some(
            (s: any) => s.company_name === editableSupplierName.trim()
          );
        }

        // Check if it's a local supplier
        if (requestData.analysis_result && requestData.analysis_result.local_suppliers) {
          isLocalSupplier = requestData.analysis_result.local_suppliers.some(
            (s: any) => s.supplier.name === editableSupplierName.trim()
          );
        }

        // Check if it's an approved supplier
        if (requestData.selected_approved_suppliers) {
          isApprovedSupplier = requestData.selected_approved_suppliers.includes(editableSupplierName.trim());
        }
      }

      console.log('🔍 Supplier type check:', {
        name: editableSupplierName.trim(),
        isAISupplier,
        isLocalSupplier,
        isApprovedSupplier
      });

      if (isAISupplier) {
        // For AI suppliers, update in the request analysis_result
        console.log('🔍 Updating AI supplier in request analysis_result');
        
      const response = await fetch(API_ENDPOINTS.UPDATE_SUPPLIER_INFO(requestId, supplierIndex.toString()), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          supplier_name: editableSupplierName.trim(),
            supplier_email: editableSupplierEmail.trim(),
            additional_emails: editableAdditionalEmails.filter(email => email && email.trim() !== '')
        })
      });

      const result = await response.json();
        console.log('🔍 AI supplier update result:', result);

      if (result.success) {
        setIsEditingSupplier(false);
          
          // Wait a moment for backend to update the supplier info
          await new Promise(resolve => setTimeout(resolve, 1000));
          
        // Update the template with new supplier info
        await loadEmailTemplate(currentLanguage);
          
          // Show success message
          alert('AI supplier information updated successfully in request! Template has been refreshed with new supplier details.');
        } else {
          setError(result.message || t('components.email_modal.failed_update_supplier'));
        }
      } else {
        // For Local Database and Approved Suppliers, update in suppliers collection
        console.log('🔍 Updating supplier in suppliers collection');
        console.log('🔍 Supplier name:', editableSupplierName.trim());
        console.log('🔍 Supplier email:', editableSupplierEmail.trim());
        console.log('🔍 All emails:', [editableSupplierEmail.trim(), ...editableAdditionalEmails.filter(email => email && email.trim() !== '')]);
        
        const updateResponse = await fetch(API_ENDPOINTS.UPDATE_SUPPLIER_INFO(requestId, supplierIndex.toString()), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            supplier_name: editableSupplierName.trim(),
            old_supplier_name: originalSupplierName, // Send original name for finding
            email: editableSupplierEmail.trim(),
            emails: [editableSupplierEmail.trim(), ...editableAdditionalEmails.filter(email => email && email.trim() !== '')]
          })
        });

        console.log('🔍 Update response status:', updateResponse.status);
        const updateResult = await updateResponse.json();
        console.log('🔍 Supplier update result:', updateResult);
        
        if (updateResponse.ok) {
          if (updateResult.success) {
            setIsEditingSupplier(false);
            
            // Update original name to new name for future updates
            setOriginalSupplierName(editableSupplierName.trim());
            
            // Show success message
            alert('Supplier information updated successfully in database!');
          } else {
            setError(updateResult.message || 'Failed to update supplier in database');
          }
        } else {
          setError(`Failed to update supplier in database: ${updateResult.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error updating supplier:', error);
      setError(t('components.email_modal.error_update_supplier'));
    } finally {
      setIsUpdatingSupplier(false);
    }
  };

  const handleSubmit = async () => {
    if (!emailSubject || !emailSubject.trim() || !emailContent || !emailContent.trim()) {
      setError(t('components.email_modal.email_subject_content_required'));
      return;
    }

    if (selectedRecipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    // Show confirmation popup before sending
    const confirmMessage = `Are you sure you want to send this email to the following recipients?\n\nRecipients:\n${selectedRecipients.join('\n')}\n\nSubject: ${emailSubject || ''}\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return; // User cancelled
    }

    setIsSending(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.SEND_SUPPLIER_EMAIL(requestId, supplierIndex.toString()), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email_subject: emailSubject || '',
          email_content: emailContent || '',
          recipient_emails: selectedRecipients,
          supplier_name: editableSupplierName,
          supplier_email: editableSupplierEmail,
          additional_emails: editableAdditionalEmails.filter(email => email && email.trim() !== '')
        })
      });

      const result = await response.json();

      if (result.success) {
        onEmailSent();
        onClose();
        setEmailSubject('');
        setEmailContent('');
      } else {
        // Handle specific error cases
        if (result.error && result.error.includes('No suppliers available')) {
          setError('No suppliers available. Please run analysis first to get supplier recommendations.');
        } else {
          setError(result.message || result.error || t('components.email_modal.failed_send_email'));
        }
      }
    } catch (error) {
      setError(t('components.email_modal.error_send_email'));
    } finally {
      setIsSending(false);
    }
  };

  // Handle recipient selection
  const handleRecipientToggle = (email: string) => {
    setSelectedRecipients(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const handleSelectAllRecipients = () => {
    setSelectedRecipients([...allAvailableEmails]);
  };

  const handleDeselectAllRecipients = () => {
    setSelectedRecipients([]);
  };

  // Handle additional emails for supplier editing
  const handleAddAdditionalEmail = () => {
    setEditableAdditionalEmails(prev => [...prev, '']);
  };

  const handleRemoveAdditionalEmail = (index: number) => {
    setEditableAdditionalEmails(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateAdditionalEmail = (index: number, value: string) => {
    setEditableAdditionalEmails(prev => 
      prev.map((email, i) => i === index ? value : email)
    );
  };

  const handleClose = () => {
    setEmailSubject('');
    setEmailContent('');
    setError('');
    setCurrentLanguage('en');
    setIsEditingSupplier(false);
    setEditableSupplierName(supplierName);
    setEditableSupplierEmail(supplierEmail);
    setEditableAdditionalEmails([]);
    setSelectedRecipients([]);
    setAllAvailableEmails([]);
    onClose();
  };

  // Load template when modal opens
  useEffect(() => {
    if (isOpen) {
      // Don't send language parameter on initial load - let backend determine based on region
      loadEmailTemplate();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="email-modal-overlay" onClick={handleClose}>
      <div className="email-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="email-modal-header">
          <h2>{t('components.email_modal.title')}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="email-modal-body">
          <div className="supplier-info">
            <div className="supplier-header">
              <h3>{t('components.email_modal.supplier_details')}</h3>
              <button 
                className="edit-supplier-btn"
                onClick={() => setIsEditingSupplier(!isEditingSupplier)}
                disabled={isUpdatingSupplier}
              >
                {isEditingSupplier ? t('components.email_modal.cancel_edit') : t('common.edit')}
              </button>
            </div>
            
            {isEditingSupplier ? (
              <div className="supplier-edit-form">
                <div className="form-group">
                  <label htmlFor="supplier-name">{t('common.name')} *</label>
                  <input
                    id="supplier-name"
                    type="text"
                    value={editableSupplierName}
                    onChange={(e) => setEditableSupplierName(e.target.value)}
                                          placeholder={t('components.email_modal.enter_supplier_name')}
                    className="supplier-input"
                    disabled={isUpdatingSupplier}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="supplier-email">{t('common.email')} *</label>
                  <input
                    id="supplier-email"
                    type="email"
                    value={editableSupplierEmail}
                    onChange={(e) => setEditableSupplierEmail(e.target.value)}
                                          placeholder={t('components.email_modal.enter_supplier_email')}
                    className="supplier-input"
                    disabled={isUpdatingSupplier}
                  />
                </div>
                
                {/* Additional Emails Section */}
                <div className="form-group">
                  <div className="additional-emails-header">
                    <label>Additional Emails</label>
                    <button
                      type="button"
                      className="add-email-btn"
                      onClick={handleAddAdditionalEmail}
                      disabled={isUpdatingSupplier}
                    >
                      + Add Email
                    </button>
                  </div>
                  
                  {editableAdditionalEmails.map((email, index) => (
                    <div key={index} className="additional-email-row">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => handleUpdateAdditionalEmail(index, e.target.value)}
                        placeholder="Enter additional email address"
                        className="supplier-input additional-email-input"
                        disabled={isUpdatingSupplier}
                      />
                      <button
                        type="button"
                        className="remove-email-btn"
                        onClick={() => handleRemoveAdditionalEmail(index)}
                        disabled={isUpdatingSupplier}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  
                  {editableAdditionalEmails.length === 0 && (
                    <p className="no-additional-emails">
                      No additional emails added yet. Click "Add Email" to add more email addresses.
                    </p>
                  )}
                </div>
                <div className="supplier-edit-actions">
                  <button
                    className="update-supplier-btn"
                    onClick={handleUpdateSupplier}
                    disabled={isUpdatingSupplier || !editableSupplierName || !editableSupplierName.trim() || !editableSupplierEmail || !editableSupplierEmail.trim()}
                  >
                    {isUpdatingSupplier ? t('common.update') + '...' : t('components.email_modal.update_supplier')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="supplier-display">
                <p><strong>{t('common.name')}:</strong> {editableSupplierName}</p>
                <p><strong>{t('common.email')}:</strong> {editableSupplierEmail}</p>
                {editableAdditionalEmails.length > 0 && (
                  <div className="additional-emails-display">
                    <p><strong>Additional Emails:</strong></p>
                    <ul>
                      {editableAdditionalEmails.map((email, index) => (
                        <li key={index}>{email}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            <div className="language-info">
              <span className="language-badge">
                {currentLanguage === 'en' ? t('components.email_modal.english') : 
                 currentLanguage === 'de' ? t('components.email_modal.german') : 
                 t('components.email_modal.turkish')}
              </span>
            </div>
          </div>

          {/* Multi-recipient selection */}
          {allAvailableEmails.length > 1 && (
            <div className="recipient-selection">
              <div className="recipient-selection-header">
                <h4>Select Recipients</h4>
                <div className="recipient-actions">
                  <button 
                    type="button" 
                    className="select-all-btn"
                    onClick={handleSelectAllRecipients}
                  >
                    Select All
                  </button>
                  <button 
                    type="button" 
                    className="deselect-all-btn"
                    onClick={handleDeselectAllRecipients}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="recipient-list">
                {allAvailableEmails.map((email, index) => (
                  <label key={index} className="recipient-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.includes(email)}
                      onChange={() => handleRecipientToggle(email)}
                    />
                    <span className="checkmark"></span>
                    <span className="email-address">{email}</span>
                  </label>
                ))}
              </div>
              <div className="selected-count">
                {selectedRecipients.length} of {allAvailableEmails.length} recipients selected
              </div>
            </div>
          )}

          {isLoadingTemplate && (
            <div className="loading-template">
              <div className="loading-spinner">{t('components.email_modal.loading_email_template')}</div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email-subject">{t('common.subject')} *</label>
            <input
              id="email-subject"
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
                              placeholder={t('components.email_modal.enter_email_subject')}
              className="email-subject-input"
              disabled={isLoadingTemplate}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email-content">{t('common.email_content')} *</label>
            <textarea
              id="email-content"
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
                              placeholder={t('components.email_modal.enter_email_content')}
              rows={12}
              className="email-content-input"
              disabled={isLoadingTemplate}
            />
            <div className="email-preview">
              <div 
                className="preview-content"
                dangerouslySetInnerHTML={{ __html: emailContent }}
              />
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="email-modal-footer">
          <button
            className="change-language-btn"
            onClick={handleChangeLanguage}
            disabled={isSending || isLoadingTemplate}
          >
            {currentLanguage === 'en' ? t('components.email_modal.german') : 
             currentLanguage === 'de' ? t('components.email_modal.turkish') : 
             t('components.email_modal.english')}
          </button>
          <button
            className="cancel-btn"
            onClick={handleClose}
            disabled={isSending}
          >
            {t('common.cancel')}
          </button>
          <button
            className="send-btn"
            onClick={handleSubmit}
            disabled={isSending || isLoadingTemplate || !emailSubject || !emailSubject.trim() || !emailContent || !emailContent.trim()}
          >
            {isSending ? t('common.sending') : t('common.send') + ' ' + t('common.email')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailModal; 